import { getLoggedUser, loginUrl } from "@/lib/api/auth"
import { User, UserAuthorizations } from "@/lib/types/user"
import { getBrowserLocale, type LocaleType } from "@/plugins/i18n"
import { defineStore } from "pinia"

const STORAGE_KEY = "auth_session"

// Store initialization promise - shared across calls
let storesInitPromise: Promise<void> | null = null

async function initializeAppStores(): Promise<void> {
    if (storesInitPromise) return storesInitPromise

    // Dynamic imports to avoid circular dependencies
    const { useResourcesStore } = await import("@/store/resource")
    const { useUsersStore } = await import("@/store/user")
    const { useCountriesStore } = await import("@/store/country")
    const { useCommercialSegmentsStore } = await import("@/store/commercialSegment")

    storesInitPromise = Promise.all([
        useResourcesStore()._initialize(),
        useUsersStore()._initialize(),
        useCountriesStore().fetchAll(),
        useCommercialSegmentsStore().fetchAll()
    ]).then(() => { })

    return storesInitPromise
}

interface AuthState {
    user: User | null
    language: LocaleType
    accessToken: string | null
    idToken: string | null
    expiresAt: number | null
    loading: boolean
}

export const useAuthStore = defineStore("auth", {
    state: (): AuthState => ({
        user: null,
        language: getBrowserLocale(),
        accessToken: null,
        idToken: null,
        expiresAt: null,
        loading: false,
    }),

    getters: {
        /** Verifica se l'utente è autenticato */
        isAuthenticated(state): boolean {
            return !!state.accessToken && !!state.expiresAt && state.expiresAt > Date.now()
        },

        /** Verifica se il token sta per scadere (entro 5 minuti) */
        isTokenExpiring(state): boolean {
            if (!state.expiresAt) return false
            const fiveMinutes = 5 * 60 * 1000
            return state.expiresAt - Date.now() < fiveMinutes
        },

        hasAnyAuthorization(state): (requiredRoles: string[]) => boolean {
            return (requiredRoles: string[]): boolean => {
                if (!state.user) return false
                const requiredAuthorizations = requiredRoles.map(r => UserAuthorizations[r as keyof typeof UserAuthorizations])
                return requiredAuthorizations.some(ra => state.user!.permissions.includes(ra))
            }
        },

        hasAuthorization(state): (requiredRole: string) => boolean {
            return (requiredRole: string): boolean => {
                if (!state.user) return false
                const requiredAuthorization = UserAuthorizations[requiredRole as keyof typeof UserAuthorizations]
                return state.user!.permissions.includes(requiredAuthorization)
            }
        },
    },

    actions: {
        /** Reindirizza alla pagina di login OIDC */
        loginRedirect() {
            window.location.href = loginUrl
        },

        /** Gestisci callback OIDC */
        async handleAuthCallback(query: Record<string, any>) {
            const accessToken = query.access_token as string | undefined
            const idToken = query.id_token as string | undefined
            const expiresIn = Number(query.expires_in ?? 0)

            if (!accessToken || !expiresIn) {
                throw new Error("Callback senza access_token o expires_in")
            }

            this.accessToken = accessToken
            this.idToken = idToken ?? null
            this.expiresAt = Date.now() + expiresIn * 1000

            // Fetch user info prima di persistere tutto insieme
            await this.fetchUser()

            // Initialize app stores after successful auth
            await initializeAppStores()

            // Pulisci URL (non lascia token in address bar/history)
            window.history.replaceState({}, document.title, "/")
        },

        /** Recupera il profilo utente con Bearer token */
        async fetchUser() {
            if (!this.accessToken) return

            this.loading = true
            try {
                this.user = await getLoggedUser()
                this.persistSession()
            } catch (error) {
                console.error("Errore nel recupero utente:", error)
                this.clearSession()
                throw error
            } finally {
                this.loading = false
            }
        },

        /** Ripristina sessione da storage */
        async restoreSession() {
            const raw = localStorage.getItem(STORAGE_KEY)
            if (!raw) {
                // Inizializza la lingua del browser se non c'è sessione
                this.language = getBrowserLocale()
                return
            }

            try {
                const parsed = JSON.parse(raw)
                this.user = parsed.user ?? null
                this.language = parsed.language ?? getBrowserLocale()
                this.accessToken = parsed.accessToken ?? null
                this.idToken = parsed.idToken ?? null
                this.expiresAt = parsed.expiresAt ?? null

                // Verifica se il token è scaduto
                if (this.expiresAt && this.expiresAt <= Date.now()) {
                    console.warn("Token scaduto, clearing session")
                    this.clearSession()
                } else {
                    // Session is valid, initialize app stores
                    await initializeAppStores()
                }
            } catch (error) {
                console.error("Errore nel parsing della sessione:", error)
                this.clearSession()
            }
            // Per test, creo una sessione finta
            /*
            this.language = getBrowserLocale()
            this.accessToken = "abcd12345"
            this.expiresAt = Date.now() + 3600 * 1000 // 1 ora
            this.idToken = null
            this.user = await getLoggedUser()
            */
        },

        /** Persisti sessione in localStorage */
        persistSession() {
            localStorage.setItem(
                STORAGE_KEY,
                JSON.stringify({
                    user: this.user,
                    language: this.language,
                    accessToken: this.accessToken,
                    idToken: this.idToken,
                    expiresAt: this.expiresAt,
                })
            )
        },

        /** Aggiorna la lingua preferita dell'utente */
        setLanguage(lang: LocaleType) {
            this.language = lang
            this.persistSession()
        },

        /** Pulisci sessione locale */
        clearSession() {
            this.user = null
            this.language = getBrowserLocale()
            this.accessToken = null
            this.idToken = null
            this.expiresAt = null
            localStorage.removeItem(STORAGE_KEY)
        },

        /** Effettua il logout */
        logout() {
            this.clearSession()
            window.location.href = "/"
        },
    },
})